/* eslint-disable jsx-a11y/anchor-is-valid */
import secondaryComponents from "../../../../../components/secondaryComponents";
import { motion } from "framer-motion";
import globalStyles from "../../../../../assets/styles/base/globalPage.module.scss";
import {
  defaultDateDisplay,
  formatCurrency,
  nFormatter,
} from "../../../../../helpers";
import { useGoalsDetails } from "./useGoalsDetails";
import primaryComponents, {
  BackButton,
} from "../../../../../components/primaryComponents";
import { ProgressBar, RoutedModal } from "../../../../../components/common";
import { DeleteIcon } from "../../../../../assets/svgIcons";
import {
  DeleteGoalsPrompt,
  GoalsForm,
} from "../../../../../components/secondaryComponents/Goals";
import { useNavigate } from "react-router-dom";
import { statusColor } from "../../../../../localdata/goalsStatus";
import React from "react";

export const GoalsDetails = () => {
  const {
    isLoadingGoals,
    goalsDetails,
    isDeleteGoal,
    isUpdateGoalsDetails,
    setIsUpdateGoalsDetails,
    setIsDeleteGoal,
  } = useGoalsDetails();
  const navigate = useNavigate();

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Goals Details"
      description="View the details of a goal set"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`${globalStyles.pageColumnGaps}`}
        >
          <div>
            <BackButton />
          </div>

          <secondaryComponents.LoaderHelper
            classNames={"mt-5"}
            isLoading={isLoadingGoals}
          >
            <div className={`${globalStyles.pageHeaders}`}>
              <div>
                <h3 className="font-weight-normal">{goalsDetails?.name}</h3>
              </div>
              <div className="d-flex flex-wrap ml-auto" style={{ gap: "10px" }}>
                <secondaryComponents.NavigateWithPermission
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey={"can_create_update_revenue_goal"}
                >
                  <primaryComponents.Button
                    classNames={"btn btn--primary"}
                    style={{ width: "140px" }}
                    onClick={() => setIsUpdateGoalsDetails(goalsDetails)}
                  >
                    Update Goal
                  </primaryComponents.Button>
                </secondaryComponents.NavigateWithPermission>

                <secondaryComponents.NavigateWithPermission
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey={"can_delete_revenue_goal"}
                >
                  <primaryComponents.Button
                    classNames={"btn btn--outline-red"}
                    style={{ width: "50px" }}
                    onClick={() => setIsDeleteGoal(goalsDetails)}
                  >
                    <DeleteIcon
                      type="aa"
                      width={20}
                      height={20}
                      color="black"
                    />
                  </primaryComponents.Button>
                </secondaryComponents.NavigateWithPermission>
              </div>
            </div>

            <div className={`${globalStyles.cardWithBorder} white`}>
              <p>Progress</p>
              <div className={`${globalStyles.cardWithBorder} card-plain`}>
                <ProgressBar
                  progress={goalsDetails?.revenue_percentage}
                  width="100%"
                />

                <div className="d-flex justify-content-between aligni-items-center flex-wrap mt-2">
                  <h5 className="mb-0">
                    <span>
                      {formatCurrency({
                        value: goalsDetails?.revenue_amount,
                        currencyCode: goalsDetails?.store?.currency_code,
                      })}
                    </span>{" "}
                    /{" "}
                    <span className="success--text">
                      {formatCurrency({
                        value: goalsDetails?.target_amount,
                        currencyCode: goalsDetails?.store?.currency_code,
                      })}
                    </span>
                  </h5>

                  <p>
                    {goalsDetails?.revenue_percentage != null
                      ? `${nFormatter(goalsDetails?.revenue_percentage.toFixed(2), 2)}%`
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className={globalStyles.gridDetailsView}>
              <div>
                <p>Status</p>
                <p
                  className={`${globalStyles.statusBlock} ${globalStyles[statusColor[goalsDetails?.outcome]]}`}
                >
                  {goalsDetails?.outcome?.replace("_", " ") || "N/A"}
                </p>
              </div>
              <div>
                <p>Current Amount Reached</p>
                <p className="dark--text">
                  {formatCurrency({
                    value: goalsDetails?.revenue_amount,
                    currencyCode: goalsDetails?.store?.currency_code,
                  })}
                </p>
              </div>
              <div>
                <p>Target Amount</p>
                <p className="dark--text">
                  {formatCurrency({
                    value: goalsDetails?.target_amount,
                    currencyCode: goalsDetails?.store?.currency_code,
                  })}
                </p>
              </div>
              <div>
                <p>Amount Remaining</p>
                <p className="dark--text">
                  {formatCurrency({
                    value: goalsDetails?.remaining_amount,
                    currencyCode: goalsDetails?.store?.currency_code,
                  })}
                </p>
              </div>
              <div>
                <p>Goal Period</p>
                <div className="d-flex mt-2" style={{ gap: "25px" }}>
                  <div>
                    <p
                      className="smallest-text-size"
                      style={{ fontStyle: "italic" }}
                    >
                      From
                    </p>
                    <p className="smallest-text-size dark--text">
                      {defaultDateDisplay(goalsDetails?.start_date)}
                    </p>
                  </div>
                  <div>
                    <p
                      className="smallest-text-size"
                      style={{ fontStyle: "italic" }}
                    >
                      to
                    </p>
                    <p className="smallest-text-size dark--text">
                      {defaultDateDisplay(goalsDetails?.end_date)}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <p>Days Remaining</p>
                <p className="dark--text">{goalsDetails?.days_remaining}</p>
              </div>
              <div>
                <p>Avg. Per Day</p>
                <p className="dark--text">
                  {formatCurrency({
                    value: goalsDetails?.average_daily_amount,
                    currencyCode: goalsDetails?.store?.currency_code,
                  })}
                </p>
              </div>
              <div>
                <p>Amount Needed Per Day</p>
                <p className="dark--text">
                  {formatCurrency({
                    value:
                      goalsDetails?.days_remaining > 0
                        ? Number(
                            goalsDetails?.remaining_amount /
                              goalsDetails?.days_remaining,
                          )
                        : 0,
                    currencyCode: goalsDetails?.store?.currency_code,
                  })}
                </p>
              </div>

              <div>
                <p>Stores</p>
                <p className="dark--text">
                  {goalsDetails?.stores
                    ?.map((store) => store?.name)
                    ?.join(", ")}
                </p>
              </div>
              <div>
                <p>Created By</p>
                <p className="dark--text">
                  {goalsDetails?.created_by?.first_name || "N/A"}
                </p>
              </div>
              <div>
                <p>Notes</p>
                <p className="dark--text">{goalsDetails?.notes || "N/A"}</p>
              </div>
            </div>
          </secondaryComponents.LoaderHelper>

          {/* Update Goals */}
          <RoutedModal
            isActive={isUpdateGoalsDetails !== null}
            closeModal={() => {
              setIsUpdateGoalsDetails(null);
            }}
            width={1000}
            headerTitle={"Update Goal"}
            headerDescription={`Update goal target for a ${isUpdateGoalsDetails?.name}`}
          >
            <GoalsForm
              isEditMode={true}
              goalDetails={isUpdateGoalsDetails}
              closeModal={() => {
                setIsUpdateGoalsDetails(null);
              }}
            />
          </RoutedModal>
        </motion.div>

        {/* delete prompt */}
        <DeleteGoalsPrompt
          isDeleteGoal={isDeleteGoal}
          setIsDeleteGoal={setIsDeleteGoal}
          closeModal={() => navigate(-1)}
        />
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};
